import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Link, Paper, Tooltip, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { Nurse, NURSE_TYPE_THEME, ROLE_COLORS as NURSE_ROLE_COLORS, ROLE_TYPES as NURSE_ROLE_TYPES } from '../../types/nurse';
import { GenderAgeDisplay, NameNicknameDisplay } from '../common';

interface NurseCardProps {
  nurse: Nurse | null | undefined;
}

const NurseCard: React.FC<NurseCardProps> = ({ nurse }) => {
  return (
    <Paper sx={{
      p: 3,
      bgcolor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
      borderRadius: 2,
      height: '100%',
      borderLeft: `4px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`,
      position: 'relative',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="medium" sx={{ color: '#D81B60' }}>
            看護師
          </Typography>
          {nurse && nurse.role_type && (
            <Chip
              label={NURSE_ROLE_TYPES[nurse.role_type as keyof typeof NURSE_ROLE_TYPES] || nurse.role_type}
              size="small"
              sx={{
                ml: 1,
                fontWeight: 'bold',
                backgroundColor: NURSE_ROLE_COLORS[nurse.role_type as keyof typeof NURSE_ROLE_COLORS] || '#757575',
                color: 'white'
              }}
            />
          )}
        </Box>
      </Box>

      {nurse ? (
        <>
          <Tooltip title="看護師の詳細ページを表示" arrow placement="top">
            <Link
              component={RouterLink}
              to={`/nurses/${nurse.id}`}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                mb: 2,
                position: 'relative',
                padding: '8px 12px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 1px rgba(0,0,0,0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(236, 64, 122, 0.05)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  '& .nurse-name, & .nurse-nickname': {
                    color: '#D81B60',
                  },
                  '& .nurse-detail-icon': {
                    opacity: 1,
                  }
                },
                '&:active': {
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  transform: 'translateY(0px)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <NameNicknameDisplay
                  name={nurse.name}
                  nickname={nurse.nickname}
                  enableHoverEffect={true}
                  isTestAccount={nurse.is_test_account}
                  sx={{
                    '& .name-hover': { transition: 'color 0.2s ease' },
                    '& .nickname-hover': { transition: 'color 0.2s ease' }
                  }}
                />
                <LinkIcon
                  sx={{
                    opacity: 0.5,
                    transition: 'all 0.3s ease',
                    fontSize: '1.2rem',
                    color: '#D81B60'
                  }}
                  className="nurse-detail-icon"
                />
              </Box>
            </Link>
          </Tooltip>


          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
              <GenderAgeDisplay
                genderType={nurse.gender_type || ''}
                birthday={nurse.birthday || ''}
              />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                新規申し込み:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: nurse.can_assign ? 'success.main' : 'error.main'
                }}
              >
                {nurse.can_assign ? '○: 可' : '×: 不可'}
              </Typography>
            </Box>
          </Box>
        </>
      ) : (
        <Typography color="text.secondary">
          看護師情報がありません
        </Typography>
      )}
    </Paper>
  );
};
export default NurseCard;
